/**
 * Formatação pt-BR.
 *
 * `Intl` nativo, nenhuma dependência, e uma regra explícita herdada do PAN:
 * ausência de dado NUNCA vira zero nem data vazia; vira o texto "Sem dado"
 * (Playbook § Os quatro estados, quinta regra).
 */

export const SEM_DADO = 'Sem dado';

const dataCurta = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const dataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const diaMes = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long' });
const diaMesAno = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
const relativo = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

export function parseData(valor: string | null | undefined): Date | null {
  if (!valor) return null;
  // "YYYY-MM-DD" sem hora é data civil: interpretar como local, não como UTC.
  const civil = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (civil) return new Date(Number(civil[1]), Number(civil[2]) - 1, Number(civil[3]));
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

export function formatarData(valor: string | null | undefined): string {
  const data = parseData(valor);
  return data ? dataCurta.format(data) : SEM_DADO;
}

export function formatarDataHora(valor: string | null | undefined): string {
  const data = parseData(valor);
  return data ? dataHora.format(data) : SEM_DADO;
}

export function formatarHora(valor: string | null | undefined): string {
  const data = parseData(valor);
  return data ? hora.format(data) : SEM_DADO;
}

/** Texto para campo textual possivelmente ausente. Nunca devolve string vazia. */
export function texto(valor: string | null | undefined, ausente = SEM_DADO): string {
  const limpo = valor?.trim();
  return limpo ? limpo : ausente;
}

/** Plural simples pt-BR: `plural(1, 'versão', 'versões')`. */
export function plural(quantidade: number, singular: string, plural_: string): string {
  return `${quantidade.toLocaleString('pt-BR')} ${quantidade === 1 ? singular : plural_}`;
}

/** Chave do dia civil LOCAL (yyyy-mm-dd) de um instante. */
export function chaveDia(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

/**
 * "há 5 minutos", "há 3 horas", "ontem", "há 4 dias"… — o horário do card do
 * feed. Passado de 30 dias vira data curta: "há 7 meses" já não ajuda a
 * localizar uma versão, a data sim.
 */
export function formatarRelativo(valor: string | null | undefined, agora: Date = new Date()): string {
  const data = parseData(valor);
  if (!data) return SEM_DADO;
  const segundos = Math.round((data.getTime() - agora.getTime()) / 1000);
  const abs = Math.abs(segundos);
  if (abs < 45) return 'agora há pouco';
  if (abs < 60 * 60) return relativo.format(Math.round(segundos / 60), 'minute');
  if (abs < 60 * 60 * 24) return relativo.format(Math.round(segundos / 3600), 'hour');
  if (abs < 60 * 60 * 24 * 30) {
    // Dias contados por dia CIVIL — 23h de ontem para 01h de hoje é "ontem",
    // não "há 2 horas" arredondado para 0 dias.
    const dias = Math.round(
      (new Date(data.getFullYear(), data.getMonth(), data.getDate()).getTime() -
        new Date(agora.getFullYear(), agora.getMonth(), agora.getDate()).getTime()) /
        86_400_000,
    );
    return relativo.format(dias, 'day');
  }
  return dataCurta.format(data);
}

/** Rótulo do grupo de dia no feed: "Hoje", "Ontem", "21 de outubro" (com ano se não for o atual). */
export function rotuloDia(valor: string | null | undefined, agora: Date = new Date()): string {
  const data = parseData(valor);
  if (!data) return SEM_DADO;
  const hoje = chaveDia(agora);
  const ontem = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 1);
  const chave = chaveDia(data);
  if (chave === hoje) return 'Hoje';
  if (chave === chaveDia(ontem)) return 'Ontem';
  return data.getFullYear() === agora.getFullYear() ? diaMes.format(data) : diaMesAno.format(data);
}

export interface GrupoDia<T> {
  chave: string;
  rotulo: string;
  itens: T[];
}

/**
 * Agrupa uma lista JÁ ordenada (mais recente primeiro) por dia civil local,
 * preservando a ordem. Itens sem data válida caem num grupo "Sem dado" ao fim.
 */
export function agruparPorDia<T>(
  itens: T[],
  dataDe: (item: T) => string | null | undefined,
  agora: Date = new Date(),
): GrupoDia<T>[] {
  const grupos = new Map<string, GrupoDia<T>>();
  for (const item of itens) {
    const data = parseData(dataDe(item));
    const chave = data ? chaveDia(data) : 'sem-data';
    let grupo = grupos.get(chave);
    if (!grupo) {
      grupo = { chave, rotulo: data ? rotuloDia(dataDe(item), agora) : SEM_DADO, itens: [] };
      grupos.set(chave, grupo);
    }
    grupo.itens.push(item);
  }
  const lista = [...grupos.values()];
  return [...lista.filter((g) => g.chave !== 'sem-data'), ...lista.filter((g) => g.chave === 'sem-data')];
}

/**
 * Compara versões numéricas por grupo (`2.2602.78.0` > `2.2602.9.0`), que a
 * ordem alfabética erra. Grupo não numérico cai na comparação textual.
 */
export function compararVersao(a: string, b: string): number {
  const pa = a.split('.');
  const pb = b.split('.');
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? '0';
    const y = pb[i] ?? '0';
    const nx = Number(x);
    const ny = Number(y);
    if (Number.isFinite(nx) && Number.isFinite(ny)) {
      if (nx !== ny) return nx - ny;
    } else if (x !== y) {
      return x.localeCompare(y, 'pt-BR');
    }
  }
  return 0;
}

/**
 * Link de download do artefato no Nexus, no layout padrão do Maven:
 * `<base>/<grupo com "/">/<artefato>/<versão>/<artefato>-<versão>.<tipo>`.
 * Ex.: `https://nexus.nasajon.com.br/repository/erp/br/com/nasajon/nsjAdmin/2.2602.11.0/nsjAdmin-2.2602.11.0.exe`.
 */
export function urlNexus(
  item: { grupo: string; artefato: string; tipo: string; versao: string },
  base: string,
): string {
  const caminhoGrupo = item.grupo.split('.').filter(Boolean).join('/');
  const arquivo = `${item.artefato}-${item.versao}.${item.tipo}`;
  return [base.replace(/\/+$/, ''), caminhoGrupo, item.artefato, item.versao, arquivo].filter(Boolean).join('/');
}

/** Coordenada Maven completa do artefato, como o Nexus a mostra: `grupo:artefato:tipo:versao`. */
export function coordenadaNexus(item: { grupo: string; artefato: string; tipo: string; versao: string }): string {
  return [item.grupo, item.artefato, item.tipo, item.versao].filter((parte) => parte !== '').join(':');
}

/** ISO civil (`2026-10-23`) para `23/10/2026`, sem passar por `Date` (nada de fuso). Vazio se não for uma data ISO. */
export function isoParaBR(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** dd/mm/aaaa digitado para ISO civil; `null` se não for uma data real (ex.: 31/02). */
export function brParaISO(valor: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(valor.trim());
  if (!m) return null;
  const dia = Number(m[1]);
  const mes = Number(m[2]);
  const ano = Number(m[3]);
  const data = new Date(ano, mes - 1, dia);
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
  return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}
